import { Dialog as SheetPrimitive } from 'radix-ui';
import { X } from 'lucide-react';
import { cn } from '../lib/utils.js';

export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;

const sideClasses = {
  right: 'inset-y-0 right-0 h-full w-full max-w-md border-l',
  left: 'inset-y-0 left-0 h-full w-72 max-w-[85vw] border-r',
  bottom: 'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl border-t pb-[env(safe-area-inset-bottom)]',
  top: 'inset-x-0 top-0 border-b',
};

/**
 * Side panel / drawer (for short edit forms, mobile navigation, "Mehr" menu).
 * @param {import('react').ComponentProps<typeof SheetPrimitive.Content> & { side?: 'right'|'left'|'bottom'|'top', showClose?: boolean }} props
 */
export function SheetContent({ className, children, side = 'right', showClose = true, ...props }) {
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay data-slot="sheet-overlay" className="fixed inset-0 z-50 bg-black/40" />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn('fixed z-50 flex flex-col gap-4 overflow-y-auto bg-popover text-popover-foreground shadow-sm outline-none', sideClasses[side], className)}
        {...props}
      >
        {children}
        {showClose && (
          <SheetPrimitive.Close className="absolute right-3 top-3 rounded-sm p-2 text-muted-foreground hover:text-foreground">
            <X className="size-4" aria-hidden="true" />
            <span className="sr-only">Schließen</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SheetHeader({ className, ...props }) {
  return <div data-slot="sheet-header" className={cn('flex flex-col gap-1 p-4 pr-12', className)} {...props} />;
}

/** @param {import('react').ComponentProps<'div'>} props */
export function SheetFooter({ className, ...props }) {
  return <div data-slot="sheet-footer" className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />;
}

/** @param {import('react').ComponentProps<typeof SheetPrimitive.Title>} props */
export function SheetTitle({ className, ...props }) {
  return <SheetPrimitive.Title data-slot="sheet-title" className={cn('text-h3 font-semibold', className)} {...props} />;
}

/** @param {import('react').ComponentProps<typeof SheetPrimitive.Description>} props */
export function SheetDescription({ className, ...props }) {
  return <SheetPrimitive.Description data-slot="sheet-description" className={cn('text-sm text-muted-foreground', className)} {...props} />;
}
